import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { FeatureSpecsEntities } from 'client/data/models/vehicle-v2';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { numberWithCommasForString } from 'site-modules/shared/utils/string';
import { FEATURE_DISPLAY_NAMES, NON_NUMBER_FEATURES } from 'site-modules/features-specs/constants/features-specs';
import { FEATURE_SPEC_NAME_TOOLTIP_MAP, FEATURE_SPEC_TOOLTIP } from 'site-modules/shared/constants/features-specs';
import { TooltipItem } from 'site-modules/shared/components/tooltip-item/tooltip-item';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';

export function TableDataRow({ feature: { name, value, price, description }, keyword }) {
  const onTooltipOpen = useCallback(({ id }) => {
    if (id.includes('-mp-ge')) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_VIEW_CONTENT,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
          value: 'MPGe',
        },
      });
    }
  }, []);
  let tdContent = 'Standard';

  if (value || typeof price === 'number') {
    tdContent = (
      <Fragment>
        {typeof value === 'string' && !NON_NUMBER_FEATURES.includes(name) ? numberWithCommasForString(value) : value}{' '}
        {typeof price === 'number' ? `${price >= 0 ? '+' : ''}${formatPriceString(price)}` : ''}
      </Fragment>
    );
  }

  const displayName = FEATURE_DISPLAY_NAMES[name] || name;

  return (
    <tr key={name}>
      <th scope="row" className="py-0_75 ps-0 pe-0_5">
        {keyword ? (
          <ContentFragment componentToUse="span">
            {displayName.replaceAll(
              new RegExp(`${keyword}`, 'gi'),
              str => `<span class="bg-blue-90 py-0_25">${str}</span>`
            )}
          </ContentFragment>
        ) : (
          displayName
        )}
        {!!FEATURE_SPEC_NAME_TOOLTIP_MAP[name] && (
          <TooltipItem
            id={`${kebabCase(name)}-tooltip`}
            className="top-0"
            item={{
              text: FEATURE_SPEC_TOOLTIP[FEATURE_SPEC_NAME_TOOLTIP_MAP[name]],
              placement: 'top',
              boundariesElement: 'viewport',
            }}
            useUniqId
            onTooltipOpen={onTooltipOpen}
          />
        )}
        {!!description && <div className="small">{description}</div>}
      </th>
      <td className="py-0_75 ps-0_5 pe-0" key="data-cell">
        {tdContent}
      </td>
    </tr>
  );
}

TableDataRow.propTypes = {
  feature: FeatureSpecsEntities.Feature.isRequired,
  keyword: PropTypes.string,
};

TableDataRow.defaultProps = {
  keyword: null,
};
